export interface SaunaWidgetConfig {
  /** Home whose sauna to render. `null` until a home is picked or auto-resolved. */
  homeId: string | null;
  /** Default refetch interval in seconds. Adaptive polling tightens to 15s while warming/ready. */
  pollSeconds: number;
  /** Days of history shown in the detail modal chart and optional in-tile sparkline. */
  historyDays: number;
  /** When true, show a small sparkline in the tile (when enough points are available). */
  showHistory: boolean;
  /** Play a pleasant chime when phase first transitions into `ready`. */
  soundOnReady: boolean;
  /** Which chime to play: a recorded sauna sample, or a synthesised two-note bell. */
  chimeSound: SaunaChimeSound;
}

export type SaunaChimeSound = 'sample' | 'tone';
export const SAUNA_CHIME_SOUNDS: SaunaChimeSound[] = ['sample', 'tone'];

export const POLL_SECONDS_MIN = 10;
export const POLL_SECONDS_MAX = 300;
export const HISTORY_DAYS_MIN = 1;
export const HISTORY_DAYS_MAX = 7;

export const DEFAULT_SAUNA_WIDGET_CONFIG: SaunaWidgetConfig = {
  homeId: null,
  pollSeconds: 30,
  historyDays: 1,
  showHistory: true,
  soundOnReady: true,
  chimeSound: 'sample',
};

export function mergeSaunaWidgetConfig(partial: Partial<SaunaWidgetConfig> | null | undefined): SaunaWidgetConfig {
  const p = partial ?? {};
  const homeId = typeof p.homeId === 'string' && p.homeId !== '' ? p.homeId : null;

  const rawPoll =
    typeof p.pollSeconds === 'number' && Number.isFinite(p.pollSeconds) ? p.pollSeconds : DEFAULT_SAUNA_WIDGET_CONFIG.pollSeconds;
  const pollSeconds = Math.min(Math.max(Math.round(rawPoll), POLL_SECONDS_MIN), POLL_SECONDS_MAX);

  const rawDays =
    typeof p.historyDays === 'number' && Number.isFinite(p.historyDays) ? p.historyDays : DEFAULT_SAUNA_WIDGET_CONFIG.historyDays;
  const historyDays = Math.min(Math.max(Math.round(rawDays), HISTORY_DAYS_MIN), HISTORY_DAYS_MAX);

  const showHistory = typeof p.showHistory === 'boolean' ? p.showHistory : DEFAULT_SAUNA_WIDGET_CONFIG.showHistory;
  const soundOnReady = typeof p.soundOnReady === 'boolean' ? p.soundOnReady : DEFAULT_SAUNA_WIDGET_CONFIG.soundOnReady;
  const chimeSound: SaunaChimeSound =
    p.chimeSound === 'tone' || p.chimeSound === 'sample' ? p.chimeSound : DEFAULT_SAUNA_WIDGET_CONFIG.chimeSound;

  return { homeId, pollSeconds, historyDays, showHistory, soundOnReady, chimeSound };
}
